/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Fuze Portals API
 * Backend API for Fuze's program portals (currently Onward, UCB's FINTEPLA REMS program). Only health and browser sign-in routes are public. Every other route requires a valid session; missing or invalid sessions return 401 before CSRF or input validation. Ordinary resources also require an active linked portal account. Sessions without an account can use `/me`, POST `/onboarding/account`, GET `/prescribers/{npi}`, and POST `/auth/logout`. Inactive accounts return 403 PORTAL_ACCOUNT_INACTIVE except on POST logout. Every POST, PUT, PATCH and DELETE requires the configured frontend Origin and public header `X-CSRF-Token: 1`; no token retrieval is needed. Sign-in, sign-up and the callback are browser navigations at `/api/auth/login`, `/api/auth/signup` and `/api/auth/callback`. Callback completion failures return to `/login?error=login_failed` on the portal origin. Sign out using POST `/api/auth/logout`, then navigate to the returned Auth0 URL.
 * OpenAPI spec version: 0.0.0
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import type {
  AssessmentView,
  CertificationStatus,
  CreateAccountInput,
  CreateExampleInput,
  CreatePrescriberClinicInput,
  CurrentUser,
  EnrollmentClinicChoices,
  EnrollmentConflict,
  EnrollmentForm,
  EnrollmentRejection,
  EnrollmentSigningSession,
  EnrollmentSubmission,
  EnrollmentView,
  ErrorResponse,
  Example,
  GetHealth200,
  LoginCallbackParams,
  LoginParams,
  LogoutResponse,
  PracticeClinicChoice,
  Prescriber,
  RecordEnrollmentMaterialOpenedInput,
  SaveEnrollmentAssessmentDraftInput,
  SaveEnrollmentClinicInfoInput,
  SaveEnrollmentOfficeContactInput,
  SaveEnrollmentPrescriberInfoInput,
  SaveEnrollmentSignatureDraftInput,
  SearchEnrollmentClinicsParams,
  StartEnrollmentAssessmentInput,
  StartEnrollmentSigningInput,
  SubmitEnrollmentAssessmentInput,
  SubmitPrescriberEnrollmentInput,
} from './fuze-portals-api.schemas';

import { apiFetch } from '../mutator';
import type { ErrorType } from '../mutator';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

export const getGetHealthUrl = () => {
  return `/healthz`;
};

/**
 * Liveness probe. Returns 200 while the process is up; does not check the database.
 * @summary Health check
 */
export const getHealth = async (options?: Parameters<typeof apiFetch>[1]): Promise<GetHealth200> => {
  return apiFetch<GetHealth200>(getGetHealthUrl(), {
    ...options,
    method: 'GET',
  });
};

export const getGetHealthQueryKey = () => {
  return [`/healthz`] as const;
};

export const getGetHealthQueryOptions = <
  TData = Awaited<ReturnType<typeof getHealth>>,
  TError = ErrorType<ErrorResponse>,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getHealth>>, TError, TData>>;
  request?: SecondParameter<typeof apiFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetHealthQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getHealth>>> = ({ signal }) =>
    getHealth({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getHealth>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetHealthQueryResult = NonNullable<Awaited<ReturnType<typeof getHealth>>>;
export type GetHealthQueryError = ErrorType<ErrorResponse>;

export function useGetHealth<TData = Awaited<ReturnType<typeof getHealth>>, TError = ErrorType<ErrorResponse>>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getHealth>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<Awaited<ReturnType<typeof getHealth>>, TError, Awaited<ReturnType<typeof getHealth>>>,
        'initialData'
      >;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetHealth<TData = Awaited<ReturnType<typeof getHealth>>, TError = ErrorType<ErrorResponse>>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getHealth>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getHealth>>,
          TError,
          Awaited<ReturnType<typeof getHealth>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetHealth<TData = Awaited<ReturnType<typeof getHealth>>, TError = ErrorType<ErrorResponse>>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getHealth>>, TError, TData>>;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Health check
 */

export function useGetHealth<TData = Awaited<ReturnType<typeof getHealth>>, TError = ErrorType<ErrorResponse>>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getHealth>>, TError, TData>>;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetHealthQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

export const getLoginUrl = (params?: LoginParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/auth/login?${stringifiedParams}` : `/auth/login`;
};

/**
 * Navigate the browser to this URL on the portal origin to start Auth0 sign-in. Sets an HttpOnly transaction cookie and redirects to Auth0. Use browser navigation rather than fetch. Auth0 returns the browser to /api/auth/callback to complete sign-in.
 * @summary Start browser sign-in
 */
export const login = async (params?: LoginParams, options?: Parameters<typeof apiFetch>[1]): Promise<unknown> => {
  return apiFetch<unknown>(getLoginUrl(params), {
    ...options,
    method: 'GET',
  });
};

export const getLoginQueryKey = (params?: LoginParams) => {
  return [`/auth/login`, ...(params ? [params] : [])] as const;
};

export const getLoginQueryOptions = <
  TData = Awaited<ReturnType<typeof login>>,
  TError = ErrorType<void | ErrorResponse>,
>(
  params?: LoginParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof login>>, TError, TData>>;
    request?: SecondParameter<typeof apiFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getLoginQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof login>>> = ({ signal }) =>
    login(params, { signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<Awaited<ReturnType<typeof login>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type LoginQueryResult = NonNullable<Awaited<ReturnType<typeof login>>>;
export type LoginQueryError = ErrorType<void | ErrorResponse>;

export function useLogin<TData = Awaited<ReturnType<typeof login>>, TError = ErrorType<void | ErrorResponse>>(
  params: undefined | LoginParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof login>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<Awaited<ReturnType<typeof login>>, TError, Awaited<ReturnType<typeof login>>>,
        'initialData'
      >;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useLogin<TData = Awaited<ReturnType<typeof login>>, TError = ErrorType<void | ErrorResponse>>(
  params?: LoginParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof login>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<Awaited<ReturnType<typeof login>>, TError, Awaited<ReturnType<typeof login>>>,
        'initialData'
      >;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useLogin<TData = Awaited<ReturnType<typeof login>>, TError = ErrorType<void | ErrorResponse>>(
  params?: LoginParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof login>>, TError, TData>>;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Start browser sign-in
 */

export function useLogin<TData = Awaited<ReturnType<typeof login>>, TError = ErrorType<void | ErrorResponse>>(
  params?: LoginParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof login>>, TError, TData>>;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getLoginQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

export const getLoginCallbackUrl = (params?: LoginCallbackParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/auth/callback?${stringifiedParams}` : `/auth/callback`;
};

/**
 * Auth0 navigates the browser here after sign-in or sign-up; the frontend should not call this route directly. On success, creates the session cookie and returns to the portal's saved returnTo, or /. If callback completion fails, including cancellation or an expired login transaction, redirects to /login?error=login_failed on the portal origin with Cache-Control: no-store. Auth0 error details are not forwarded. The frontend should show a sign-in error and offer a retry via /api/auth/login instead of automatically restarting authentication.
 * @summary Complete browser sign-in or sign-up
 */
export const loginCallback = async (
  params?: LoginCallbackParams,
  options?: Parameters<typeof apiFetch>[1],
): Promise<unknown> => {
  return apiFetch<unknown>(getLoginCallbackUrl(params), {
    ...options,
    method: 'GET',
  });
};

export const getLoginCallbackQueryKey = (params?: LoginCallbackParams) => {
  return [`/auth/callback`, ...(params ? [params] : [])] as const;
};

export const getLoginCallbackQueryOptions = <
  TData = Awaited<ReturnType<typeof loginCallback>>,
  TError = ErrorType<void | ErrorResponse>,
>(
  params?: LoginCallbackParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof loginCallback>>, TError, TData>>;
    request?: SecondParameter<typeof apiFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getLoginCallbackQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof loginCallback>>> = ({ signal }) =>
    loginCallback(params, { signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof loginCallback>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type LoginCallbackQueryResult = NonNullable<Awaited<ReturnType<typeof loginCallback>>>;
export type LoginCallbackQueryError = ErrorType<void | ErrorResponse>;

export function useLoginCallback<
  TData = Awaited<ReturnType<typeof loginCallback>>,
  TError = ErrorType<void | ErrorResponse>,
>(
  params: undefined | LoginCallbackParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof loginCallback>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof loginCallback>>,
          TError,
          Awaited<ReturnType<typeof loginCallback>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useLoginCallback<
  TData = Awaited<ReturnType<typeof loginCallback>>,
  TError = ErrorType<void | ErrorResponse>,
>(
  params?: LoginCallbackParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof loginCallback>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof loginCallback>>,
          TError,
          Awaited<ReturnType<typeof loginCallback>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useLoginCallback<
  TData = Awaited<ReturnType<typeof loginCallback>>,
  TError = ErrorType<void | ErrorResponse>,
>(
  params?: LoginCallbackParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof loginCallback>>, TError, TData>>;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Complete browser sign-in or sign-up
 */

export function useLoginCallback<
  TData = Awaited<ReturnType<typeof loginCallback>>,
  TError = ErrorType<void | ErrorResponse>,
>(
  params?: LoginCallbackParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof loginCallback>>, TError, TData>>;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getLoginCallbackQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

export const getLogoutUrl = () => {
  return `/auth/logout`;
};

/**
 * Revokes the current server session and clears its cookie. Send X-CSRF-Token: 1 and the trusted Origin. Navigate the browser to the returned Auth0 logout URL to finish signing out. GET sign-out is disabled.
 * @summary Sign out of the current session
 */
export const logout = async (options?: Parameters<typeof apiFetch>[1]): Promise<LogoutResponse> => {
  return apiFetch<LogoutResponse>(getLogoutUrl(), {
    ...options,
    method: 'POST',
  });
};

export const getLogoutMutationKey = () => ['logout'] as const;

export const getLogoutMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<Awaited<ReturnType<typeof logout>>, TError, void, TContext>;
  request?: SecondParameter<typeof apiFetch>;
}): UseMutationOptions<Awaited<ReturnType<typeof logout>>, TError, void, TContext> => {
  const mutationKey = getLogoutMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<Awaited<ReturnType<typeof logout>>, void> = () => {
    return logout(requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type LogoutMutationResult = NonNullable<Awaited<ReturnType<typeof logout>>>;

export type LogoutMutationError = ErrorType<ErrorResponse>;

/**
 * @summary Sign out of the current session
 */
export const useLogout = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<Awaited<ReturnType<typeof logout>>, TError, void, TContext>;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<Awaited<ReturnType<typeof logout>>, TError, void, TContext> => {
  return useMutation(getLogoutMutationOptions(options), queryClient);
};

export const getGetCurrentUserUrl = () => {
  return `/me`;
};

/**
 * Who is signed in: email, name, and the current portal account role. The account is absent when no identity link exists, so the frontend can start onboarding. Account presence does not establish a prescriber claim or certification. 401 when there is no valid session, with code `SESSION_EXPIRED` when it timed out; 403 with `PORTAL_ACCOUNT_INACTIVE` when the linked account is inactive.
 * @summary Get the signed-in user
 */
export const getCurrentUser = async (options?: Parameters<typeof apiFetch>[1]): Promise<CurrentUser> => {
  return apiFetch<CurrentUser>(getGetCurrentUserUrl(), {
    ...options,
    method: 'GET',
  });
};

export const getGetCurrentUserQueryKey = () => {
  return [`/me`] as const;
};

export const getGetCurrentUserQueryOptions = <
  TData = Awaited<ReturnType<typeof getCurrentUser>>,
  TError = ErrorType<ErrorResponse>,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCurrentUser>>, TError, TData>>;
  request?: SecondParameter<typeof apiFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetCurrentUserQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getCurrentUser>>> = ({ signal }) =>
    getCurrentUser({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getCurrentUser>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetCurrentUserQueryResult = NonNullable<Awaited<ReturnType<typeof getCurrentUser>>>;
export type GetCurrentUserQueryError = ErrorType<ErrorResponse>;

export function useGetCurrentUser<
  TData = Awaited<ReturnType<typeof getCurrentUser>>,
  TError = ErrorType<ErrorResponse>,
>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCurrentUser>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getCurrentUser>>,
          TError,
          Awaited<ReturnType<typeof getCurrentUser>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetCurrentUser<
  TData = Awaited<ReturnType<typeof getCurrentUser>>,
  TError = ErrorType<ErrorResponse>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCurrentUser>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getCurrentUser>>,
          TError,
          Awaited<ReturnType<typeof getCurrentUser>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetCurrentUser<
  TData = Awaited<ReturnType<typeof getCurrentUser>>,
  TError = ErrorType<ErrorResponse>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCurrentUser>>, TError, TData>>;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get the signed-in user
 */

export function useGetCurrentUser<
  TData = Awaited<ReturnType<typeof getCurrentUser>>,
  TError = ErrorType<ErrorResponse>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getCurrentUser>>, TError, TData>>;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetCurrentUserQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

export const getCreateAccountUrl = () => {
  return `/onboarding/account`;
};

/**
 * What Continue does on the NPI step: creates the portal account (role `prescriber`) for the signed-in user and links it to the prescriber the NPI belongs to. Returns the current user with the new `account` (201). Checks run in this order: session (401), input (400), the signed-in user already has an account (200 with that account when it is for the same NPI, so a retry is safe; 409, `ACCOUNT_ALREADY_EXISTS`, when it is for a different NPI), UCB does not have the NPI (404, `NPI_NOT_FOUND`), the NPI belongs to another user's account (409, `NPI_ALREADY_REGISTERED`). The database enforces one account per user and one per NPI, so two simultaneous requests cannot both create one.
 * @summary Create the portal account
 */
export const createAccount = async (
  createAccountInput: CreateAccountInput,
  options?: Parameters<typeof apiFetch>[1],
): Promise<CurrentUser> => {
  const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
  return apiFetch<CurrentUser>(getCreateAccountUrl(), {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(createAccountInput),
  });
};

export const getCreateAccountMutationKey = () => ['createAccount'] as const;

export const getCreateAccountMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createAccount>>,
    TError,
    CreateAccountMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof apiFetch>;
}): UseMutationOptions<Awaited<ReturnType<typeof createAccount>>, TError, CreateAccountMutationVariables, TContext> => {
  const mutationKey = getCreateAccountMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<Awaited<ReturnType<typeof createAccount>>, CreateAccountMutationVariables> = (
    props,
  ) => {
    const { data } = props ?? {};

    return createAccount(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateAccountMutationResult = NonNullable<Awaited<ReturnType<typeof createAccount>>>;
export type CreateAccountMutationBody = CreateAccountInput;
export type CreateAccountMutationError = ErrorType<ErrorResponse>;
export type CreateAccountMutationVariables = { data: CreateAccountInput };

/**
 * @summary Create the portal account
 */
export const useCreateAccount = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createAccount>>,
      TError,
      CreateAccountMutationVariables,
      TContext
    >;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<Awaited<ReturnType<typeof createAccount>>, TError, CreateAccountMutationVariables, TContext> => {
  return useMutation(getCreateAccountMutationOptions(options), queryClient);
};

export const getSignupUrl = () => {
  return `/auth/signup`;
};

/**
 * Navigate the browser to this URL on the portal origin to open Auth0's sign-up screen. Sets an HttpOnly transaction cookie and uses the same /api/auth/callback as sign-in. Use browser navigation rather than fetch. Successful sign-up returns to the portal root; callback completion failures redirect to /login?error=login_failed.
 * @summary Start browser sign-up
 */
export const signup = async (options?: Parameters<typeof apiFetch>[1]): Promise<unknown> => {
  return apiFetch<unknown>(getSignupUrl(), {
    ...options,
    method: 'GET',
  });
};

export const getSignupQueryKey = () => {
  return [`/auth/signup`] as const;
};

export const getSignupQueryOptions = <
  TData = Awaited<ReturnType<typeof signup>>,
  TError = ErrorType<void | ErrorResponse>,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof signup>>, TError, TData>>;
  request?: SecondParameter<typeof apiFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getSignupQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof signup>>> = ({ signal }) =>
    signup({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof signup>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type SignupQueryResult = NonNullable<Awaited<ReturnType<typeof signup>>>;
export type SignupQueryError = ErrorType<void | ErrorResponse>;

export function useSignup<TData = Awaited<ReturnType<typeof signup>>, TError = ErrorType<void | ErrorResponse>>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof signup>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<Awaited<ReturnType<typeof signup>>, TError, Awaited<ReturnType<typeof signup>>>,
        'initialData'
      >;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSignup<TData = Awaited<ReturnType<typeof signup>>, TError = ErrorType<void | ErrorResponse>>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof signup>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<Awaited<ReturnType<typeof signup>>, TError, Awaited<ReturnType<typeof signup>>>,
        'initialData'
      >;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSignup<TData = Awaited<ReturnType<typeof signup>>, TError = ErrorType<void | ErrorResponse>>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof signup>>, TError, TData>>;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Start browser sign-up
 */

export function useSignup<TData = Awaited<ReturnType<typeof signup>>, TError = ErrorType<void | ErrorResponse>>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof signup>>, TError, TData>>;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getSignupQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

export const getGetPrescriberUrl = (npi: string) => {
  return `/prescribers/${npi}`;
};

/**
 * Finds the prescriber an NPI belongs to in the data UCB and Anovo sent, so the user can confirm it is them before continuing. `hasAccount` says whether a portal account already exists for the NPI, so the NPI step can stop there. Checks run in this order: session (401), input (400), UCB does not have the NPI (404, `NPI_NOT_FOUND`). Never returns patient data.
 * @summary Get a prescriber by NPI
 */
export const getPrescriber = async (npi: string, options?: Parameters<typeof apiFetch>[1]): Promise<Prescriber> => {
  return apiFetch<Prescriber>(getGetPrescriberUrl(npi), {
    ...options,
    method: 'GET',
  });
};

export const getGetPrescriberQueryKey = (npi: string) => {
  return [`/prescribers/${npi}`] as const;
};

export const getGetPrescriberQueryOptions = <
  TData = Awaited<ReturnType<typeof getPrescriber>>,
  TError = ErrorType<ErrorResponse>,
>(
  npi: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPrescriber>>, TError, TData>>;
    request?: SecondParameter<typeof apiFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetPrescriberQueryKey(npi);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getPrescriber>>> = ({ signal }) =>
    getPrescriber(npi, { signal, ...requestOptions });

  return { queryKey, queryFn, enabled: npi !== null && npi !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getPrescriber>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetPrescriberQueryResult = NonNullable<Awaited<ReturnType<typeof getPrescriber>>>;
export type GetPrescriberQueryError = ErrorType<ErrorResponse>;

export function useGetPrescriber<TData = Awaited<ReturnType<typeof getPrescriber>>, TError = ErrorType<ErrorResponse>>(
  npi: string,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPrescriber>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getPrescriber>>,
          TError,
          Awaited<ReturnType<typeof getPrescriber>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetPrescriber<TData = Awaited<ReturnType<typeof getPrescriber>>, TError = ErrorType<ErrorResponse>>(
  npi: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPrescriber>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getPrescriber>>,
          TError,
          Awaited<ReturnType<typeof getPrescriber>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetPrescriber<TData = Awaited<ReturnType<typeof getPrescriber>>, TError = ErrorType<ErrorResponse>>(
  npi: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPrescriber>>, TError, TData>>;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get a prescriber by NPI
 */

export function useGetPrescriber<TData = Awaited<ReturnType<typeof getPrescriber>>, TError = ErrorType<ErrorResponse>>(
  npi: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPrescriber>>, TError, TData>>;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetPrescriberQueryOptions(npi, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

export const getCreatePrescriberClinicUrl = (npi: string) => {
  return `/prescribers/${npi}/clinics`;
};

/**
 * Available to the prescriber or office staff with current permission to prepare that prescriber's enrollment; AES users may not use this operation. Practice creates a portal clinic and its affiliation to the selected prescriber atomically. Returns 201 with the clinicId and display details, immediately available in GET /enrollments/{npi}/clinics. The frontend then saves that clinicId with PUT /enrollments/{npi}/clinic-info and the current enrollment revision. Creation does not select the clinic, alter the enrollment revision or submit to Anovo; the chosen clinic is sent with certification. UCB remains responsible for later master-data reconciliation; this operation does not merge existing clinics. Authorized retries with the same key and input return the original 201 result. Returns 404 for an unknown prescriber and 409 CLINIC_REQUEST_CONFLICT for a reused key with different input. Contract placeholder: valid requests return 501 NOT_IMPLEMENTED after session, active-account, CSRF and input checks. Target-specific permissions must be enforced before live behavior is enabled.
 * @summary Add a clinic for a prescriber's enrollment
 */
export const createPrescriberClinic = async (
  npi: string,
  createPrescriberClinicInput: CreatePrescriberClinicInput,
  options?: Parameters<typeof apiFetch>[1],
): Promise<PracticeClinicChoice> => {
  const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
  return apiFetch<PracticeClinicChoice>(getCreatePrescriberClinicUrl(npi), {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(createPrescriberClinicInput),
  });
};

export const getCreatePrescriberClinicMutationKey = () => ['createPrescriberClinic'] as const;

export const getCreatePrescriberClinicMutationOptions = <
  TError = ErrorType<ErrorResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createPrescriberClinic>>,
    TError,
    CreatePrescriberClinicMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof apiFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createPrescriberClinic>>,
  TError,
  CreatePrescriberClinicMutationVariables,
  TContext
> => {
  const mutationKey = getCreatePrescriberClinicMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createPrescriberClinic>>,
    CreatePrescriberClinicMutationVariables
  > = (props) => {
    const { npi, data } = props ?? {};

    return createPrescriberClinic(npi, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreatePrescriberClinicMutationResult = NonNullable<Awaited<ReturnType<typeof createPrescriberClinic>>>;
export type CreatePrescriberClinicMutationBody = CreatePrescriberClinicInput;
export type CreatePrescriberClinicMutationError = ErrorType<ErrorResponse>;
export type CreatePrescriberClinicMutationVariables = { npi: string; data: CreatePrescriberClinicInput };

/**
 * @summary Add a clinic for a prescriber's enrollment
 */
export const useCreatePrescriberClinic = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createPrescriberClinic>>,
      TError,
      CreatePrescriberClinicMutationVariables,
      TContext
    >;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof createPrescriberClinic>>,
  TError,
  CreatePrescriberClinicMutationVariables,
  TContext
> => {
  return useMutation(getCreatePrescriberClinicMutationOptions(options), queryClient);
};

export const getGetPrescriberEnrollmentUrl = (npi: string) => {
  return `/enrollments/${npi}`;
};

/**
 * The prescriber or office staff with current permission to prepare this prescriber's enrollment may use this operation; AES users may not. Combines Practice details with saved section values, field rules, five checklist steps and available actions. A known prescriber without a draft gets started=false; reading creates no records. Unknown prescribers return 404. Contract placeholder: valid requests return 501 NOT_IMPLEMENTED after session, active-account, CSRF and input checks. Target-specific permissions are documented here and must be enforced before live behavior is enabled.
 * @summary Read the enrollment form and saved progress
 */
export const getPrescriberEnrollment = async (
  npi: string,
  options?: Parameters<typeof apiFetch>[1],
): Promise<EnrollmentView> => {
  return apiFetch<EnrollmentView>(getGetPrescriberEnrollmentUrl(npi), {
    ...options,
    method: 'GET',
  });
};

export const getGetPrescriberEnrollmentQueryKey = (npi: string) => {
  return [`/enrollments/${npi}`] as const;
};

export const getGetPrescriberEnrollmentQueryOptions = <
  TData = Awaited<ReturnType<typeof getPrescriberEnrollment>>,
  TError = ErrorType<ErrorResponse>,
>(
  npi: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPrescriberEnrollment>>, TError, TData>>;
    request?: SecondParameter<typeof apiFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetPrescriberEnrollmentQueryKey(npi);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getPrescriberEnrollment>>> = ({ signal }) =>
    getPrescriberEnrollment(npi, { signal, ...requestOptions });

  return { queryKey, queryFn, enabled: npi !== null && npi !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getPrescriberEnrollment>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetPrescriberEnrollmentQueryResult = NonNullable<Awaited<ReturnType<typeof getPrescriberEnrollment>>>;
export type GetPrescriberEnrollmentQueryError = ErrorType<ErrorResponse>;

export function useGetPrescriberEnrollment<
  TData = Awaited<ReturnType<typeof getPrescriberEnrollment>>,
  TError = ErrorType<ErrorResponse>,
>(
  npi: string,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPrescriberEnrollment>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getPrescriberEnrollment>>,
          TError,
          Awaited<ReturnType<typeof getPrescriberEnrollment>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetPrescriberEnrollment<
  TData = Awaited<ReturnType<typeof getPrescriberEnrollment>>,
  TError = ErrorType<ErrorResponse>,
>(
  npi: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPrescriberEnrollment>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getPrescriberEnrollment>>,
          TError,
          Awaited<ReturnType<typeof getPrescriberEnrollment>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetPrescriberEnrollment<
  TData = Awaited<ReturnType<typeof getPrescriberEnrollment>>,
  TError = ErrorType<ErrorResponse>,
>(
  npi: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPrescriberEnrollment>>, TError, TData>>;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Read the enrollment form and saved progress
 */

export function useGetPrescriberEnrollment<
  TData = Awaited<ReturnType<typeof getPrescriberEnrollment>>,
  TError = ErrorType<ErrorResponse>,
>(
  npi: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPrescriberEnrollment>>, TError, TData>>;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetPrescriberEnrollmentQueryOptions(npi, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

export const getSearchEnrollmentClinicsUrl = (npi: string, params?: SearchEnrollmentClinicsParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/enrollments/${npi}/clinics?${stringifiedParams}`
    : `/enrollments/${npi}/clinics`;
};

/**
 * The prescriber or office staff with current permission to prepare this prescriber's enrollment may use this operation; AES users may not. Returns matching permitted clinics with address, phone and fax. Search is optional, trimmed and defaults to empty. Addresses distinguish same-name clinics. canAddClinic indicates permission to add a clinic with POST /prescribers/{npi}/clinics; save its returned clinicId through the clinic-info operation to select it. Contract placeholder: valid requests return 501 NOT_IMPLEMENTED after session, active-account, CSRF and input checks. Target-specific permissions are documented here and must be enforced before live behavior is enabled.
 * @summary Find permitted enrollment clinic choices
 */
export const searchEnrollmentClinics = async (
  npi: string,
  params?: SearchEnrollmentClinicsParams,
  options?: Parameters<typeof apiFetch>[1],
): Promise<EnrollmentClinicChoices> => {
  return apiFetch<EnrollmentClinicChoices>(getSearchEnrollmentClinicsUrl(npi, params), {
    ...options,
    method: 'GET',
  });
};

export const getSearchEnrollmentClinicsQueryKey = (npi: string, params?: SearchEnrollmentClinicsParams) => {
  return [`/enrollments/${npi}/clinics`, ...(params ? [params] : [])] as const;
};

export const getSearchEnrollmentClinicsQueryOptions = <
  TData = Awaited<ReturnType<typeof searchEnrollmentClinics>>,
  TError = ErrorType<ErrorResponse>,
>(
  npi: string,
  params?: SearchEnrollmentClinicsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof searchEnrollmentClinics>>, TError, TData>>;
    request?: SecondParameter<typeof apiFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getSearchEnrollmentClinicsQueryKey(npi, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof searchEnrollmentClinics>>> = ({ signal }) =>
    searchEnrollmentClinics(npi, params, { signal, ...requestOptions });

  return { queryKey, queryFn, enabled: npi !== null && npi !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof searchEnrollmentClinics>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type SearchEnrollmentClinicsQueryResult = NonNullable<Awaited<ReturnType<typeof searchEnrollmentClinics>>>;
export type SearchEnrollmentClinicsQueryError = ErrorType<ErrorResponse>;

export function useSearchEnrollmentClinics<
  TData = Awaited<ReturnType<typeof searchEnrollmentClinics>>,
  TError = ErrorType<ErrorResponse>,
>(
  npi: string,
  params: undefined | SearchEnrollmentClinicsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof searchEnrollmentClinics>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof searchEnrollmentClinics>>,
          TError,
          Awaited<ReturnType<typeof searchEnrollmentClinics>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSearchEnrollmentClinics<
  TData = Awaited<ReturnType<typeof searchEnrollmentClinics>>,
  TError = ErrorType<ErrorResponse>,
>(
  npi: string,
  params?: SearchEnrollmentClinicsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof searchEnrollmentClinics>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof searchEnrollmentClinics>>,
          TError,
          Awaited<ReturnType<typeof searchEnrollmentClinics>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSearchEnrollmentClinics<
  TData = Awaited<ReturnType<typeof searchEnrollmentClinics>>,
  TError = ErrorType<ErrorResponse>,
>(
  npi: string,
  params?: SearchEnrollmentClinicsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof searchEnrollmentClinics>>, TError, TData>>;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Find permitted enrollment clinic choices
 */

export function useSearchEnrollmentClinics<
  TData = Awaited<ReturnType<typeof searchEnrollmentClinics>>,
  TError = ErrorType<ErrorResponse>,
>(
  npi: string,
  params?: SearchEnrollmentClinicsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof searchEnrollmentClinics>>, TError, TData>>;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getSearchEnrollmentClinicsQueryOptions(npi, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

export const getSaveEnrollmentPrescriberInfoUrl = (npi: string) => {
  return `/enrollments/${npi}/prescriber-info`;
};

/**
 * The prescriber or office staff with current permission to prepare this prescriber's enrollment may use this operation; AES users may not. Replaces editable draft values in this section only. The first preparation save creates the enrollment internally. confirm=false allows partial work; confirm=true checks required fields. sourceSpecialty displays UCB's read-only specialty. specialty and specialtyOther select the certification value sent to Anovo without changing UCB's record; other source-populated values stay read-only in v1. Returns 409 ENROLLMENT_CHANGED for an outdated revision, ENROLLMENT_INCOMPLETE with missingFields for incomplete confirmation, or ENROLLMENT_FINALIZED after finalization. Changed values invalidate the old confirmation, preview and signature. Contract placeholder: valid requests return 501 NOT_IMPLEMENTED after session, active-account, CSRF and input checks. Target-specific permissions are documented here and must be enforced before live behavior is enabled.
 * @summary Save or confirm prescriber information
 */
export const saveEnrollmentPrescriberInfo = async (
  npi: string,
  saveEnrollmentPrescriberInfoInput: SaveEnrollmentPrescriberInfoInput,
  options?: Parameters<typeof apiFetch>[1],
): Promise<EnrollmentView> => {
  const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
  return apiFetch<EnrollmentView>(getSaveEnrollmentPrescriberInfoUrl(npi), {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(saveEnrollmentPrescriberInfoInput),
  });
};

export const getSaveEnrollmentPrescriberInfoMutationKey = () => ['saveEnrollmentPrescriberInfo'] as const;

export const getSaveEnrollmentPrescriberInfoMutationOptions = <
  TError = ErrorType<ErrorResponse | EnrollmentConflict>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof saveEnrollmentPrescriberInfo>>,
    TError,
    SaveEnrollmentPrescriberInfoMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof apiFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof saveEnrollmentPrescriberInfo>>,
  TError,
  SaveEnrollmentPrescriberInfoMutationVariables,
  TContext
> => {
  const mutationKey = getSaveEnrollmentPrescriberInfoMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof saveEnrollmentPrescriberInfo>>,
    SaveEnrollmentPrescriberInfoMutationVariables
  > = (props) => {
    const { npi, data } = props ?? {};

    return saveEnrollmentPrescriberInfo(npi, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type SaveEnrollmentPrescriberInfoMutationResult = NonNullable<
  Awaited<ReturnType<typeof saveEnrollmentPrescriberInfo>>
>;
export type SaveEnrollmentPrescriberInfoMutationBody = SaveEnrollmentPrescriberInfoInput;
export type SaveEnrollmentPrescriberInfoMutationError = ErrorType<ErrorResponse | EnrollmentConflict>;
export type SaveEnrollmentPrescriberInfoMutationVariables = { npi: string; data: SaveEnrollmentPrescriberInfoInput };

/**
 * @summary Save or confirm prescriber information
 */
export const useSaveEnrollmentPrescriberInfo = <
  TError = ErrorType<ErrorResponse | EnrollmentConflict>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof saveEnrollmentPrescriberInfo>>,
      TError,
      SaveEnrollmentPrescriberInfoMutationVariables,
      TContext
    >;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof saveEnrollmentPrescriberInfo>>,
  TError,
  SaveEnrollmentPrescriberInfoMutationVariables,
  TContext
> => {
  return useMutation(getSaveEnrollmentPrescriberInfoMutationOptions(options), queryClient);
};

export const getSaveEnrollmentClinicInfoUrl = (npi: string) => {
  return `/enrollments/${npi}/clinic-info`;
};

/**
 * The prescriber or office staff with current permission to prepare this prescriber's enrollment may use this operation; AES users may not. Selects a permitted clinic and replaces this section's editable draft values without changing master records. A first save creates the enrollment internally. Confirmation checks required clinic details; a clinic change can invalidate contact confirmation and old documents. Returns 409 ENROLLMENT_CHANGED, ENROLLMENT_INCOMPLETE with missingFields, or ENROLLMENT_FINALIZED. Contract placeholder: valid requests return 501 NOT_IMPLEMENTED after session, active-account, CSRF and input checks. Target-specific permissions are documented here and must be enforced before live behavior is enabled.
 * @summary Save or confirm clinic information
 */
export const saveEnrollmentClinicInfo = async (
  npi: string,
  saveEnrollmentClinicInfoInput: SaveEnrollmentClinicInfoInput,
  options?: Parameters<typeof apiFetch>[1],
): Promise<EnrollmentView> => {
  const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
  return apiFetch<EnrollmentView>(getSaveEnrollmentClinicInfoUrl(npi), {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(saveEnrollmentClinicInfoInput),
  });
};

export const getSaveEnrollmentClinicInfoMutationKey = () => ['saveEnrollmentClinicInfo'] as const;

export const getSaveEnrollmentClinicInfoMutationOptions = <
  TError = ErrorType<ErrorResponse | EnrollmentConflict>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof saveEnrollmentClinicInfo>>,
    TError,
    SaveEnrollmentClinicInfoMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof apiFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof saveEnrollmentClinicInfo>>,
  TError,
  SaveEnrollmentClinicInfoMutationVariables,
  TContext
> => {
  const mutationKey = getSaveEnrollmentClinicInfoMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof saveEnrollmentClinicInfo>>,
    SaveEnrollmentClinicInfoMutationVariables
  > = (props) => {
    const { npi, data } = props ?? {};

    return saveEnrollmentClinicInfo(npi, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type SaveEnrollmentClinicInfoMutationResult = NonNullable<Awaited<ReturnType<typeof saveEnrollmentClinicInfo>>>;
export type SaveEnrollmentClinicInfoMutationBody = SaveEnrollmentClinicInfoInput;
export type SaveEnrollmentClinicInfoMutationError = ErrorType<ErrorResponse | EnrollmentConflict>;
export type SaveEnrollmentClinicInfoMutationVariables = { npi: string; data: SaveEnrollmentClinicInfoInput };

/**
 * @summary Save or confirm clinic information
 */
export const useSaveEnrollmentClinicInfo = <TError = ErrorType<ErrorResponse | EnrollmentConflict>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof saveEnrollmentClinicInfo>>,
      TError,
      SaveEnrollmentClinicInfoMutationVariables,
      TContext
    >;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof saveEnrollmentClinicInfo>>,
  TError,
  SaveEnrollmentClinicInfoMutationVariables,
  TContext
> => {
  return useMutation(getSaveEnrollmentClinicInfoMutationOptions(options), queryClient);
};

export const getSaveEnrollmentOfficeContactUrl = (npi: string) => {
  return `/enrollments/${npi}/office-contact`;
};

/**
 * The prescriber or office staff with current permission to prepare this prescriber's enrollment may use this operation; AES users may not. Saves this contact independently, including its communication preference. sameAsPracticeContact copies the clinic phone and fax; otherwise alternate numbers are required for confirmation. A first save creates the enrollment internally. Returns 409 ENROLLMENT_CHANGED, ENROLLMENT_INCOMPLETE with missingFields, or ENROLLMENT_FINALIZED. Contract placeholder: valid requests return 501 NOT_IMPLEMENTED after session, active-account, CSRF and input checks. Target-specific permissions are documented here and must be enforced before live behavior is enabled.
 * @summary Save or confirm the primary office contact
 */
export const saveEnrollmentOfficeContact = async (
  npi: string,
  saveEnrollmentOfficeContactInput: SaveEnrollmentOfficeContactInput,
  options?: Parameters<typeof apiFetch>[1],
): Promise<EnrollmentView> => {
  const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
  return apiFetch<EnrollmentView>(getSaveEnrollmentOfficeContactUrl(npi), {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(saveEnrollmentOfficeContactInput),
  });
};

export const getSaveEnrollmentOfficeContactMutationKey = () => ['saveEnrollmentOfficeContact'] as const;

export const getSaveEnrollmentOfficeContactMutationOptions = <
  TError = ErrorType<ErrorResponse | EnrollmentConflict>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof saveEnrollmentOfficeContact>>,
    TError,
    SaveEnrollmentOfficeContactMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof apiFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof saveEnrollmentOfficeContact>>,
  TError,
  SaveEnrollmentOfficeContactMutationVariables,
  TContext
> => {
  const mutationKey = getSaveEnrollmentOfficeContactMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof saveEnrollmentOfficeContact>>,
    SaveEnrollmentOfficeContactMutationVariables
  > = (props) => {
    const { npi, data } = props ?? {};

    return saveEnrollmentOfficeContact(npi, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type SaveEnrollmentOfficeContactMutationResult = NonNullable<
  Awaited<ReturnType<typeof saveEnrollmentOfficeContact>>
>;
export type SaveEnrollmentOfficeContactMutationBody = SaveEnrollmentOfficeContactInput;
export type SaveEnrollmentOfficeContactMutationError = ErrorType<ErrorResponse | EnrollmentConflict>;
export type SaveEnrollmentOfficeContactMutationVariables = { npi: string; data: SaveEnrollmentOfficeContactInput };

/**
 * @summary Save or confirm the primary office contact
 */
export const useSaveEnrollmentOfficeContact = <
  TError = ErrorType<ErrorResponse | EnrollmentConflict>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof saveEnrollmentOfficeContact>>,
      TError,
      SaveEnrollmentOfficeContactMutationVariables,
      TContext
    >;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof saveEnrollmentOfficeContact>>,
  TError,
  SaveEnrollmentOfficeContactMutationVariables,
  TContext
> => {
  return useMutation(getSaveEnrollmentOfficeContactMutationOptions(options), queryClient);
};

export const getGetEnrollmentAssessmentUrl = (npi: string) => {
  return `/assessments/${npi}`;
};

/**
 * Prescriber-only: the signed-in account must belong to the prescriber selected by NPI. Staff, AES and other prescribers cannot use this operation. Returns the current assessment for the active enrollment, including remaining attempts and next action. Only the prescriber may read saved answers and scoring feedback. Internal enrollment and attempt history remain on the backend. Contract placeholder: valid requests return 501 NOT_IMPLEMENTED after session, active-account, CSRF and input checks. Target-specific permissions are documented here and must be enforced before live behavior is enabled.
 * @summary Read assessment materials, answers and feedback
 */
export const getEnrollmentAssessment = async (
  npi: string,
  options?: Parameters<typeof apiFetch>[1],
): Promise<AssessmentView> => {
  return apiFetch<AssessmentView>(getGetEnrollmentAssessmentUrl(npi), {
    ...options,
    method: 'GET',
  });
};

export const getGetEnrollmentAssessmentQueryKey = (npi: string) => {
  return [`/assessments/${npi}`] as const;
};

export const getGetEnrollmentAssessmentQueryOptions = <
  TData = Awaited<ReturnType<typeof getEnrollmentAssessment>>,
  TError = ErrorType<ErrorResponse>,
>(
  npi: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getEnrollmentAssessment>>, TError, TData>>;
    request?: SecondParameter<typeof apiFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetEnrollmentAssessmentQueryKey(npi);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getEnrollmentAssessment>>> = ({ signal }) =>
    getEnrollmentAssessment(npi, { signal, ...requestOptions });

  return { queryKey, queryFn, enabled: npi !== null && npi !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getEnrollmentAssessment>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetEnrollmentAssessmentQueryResult = NonNullable<Awaited<ReturnType<typeof getEnrollmentAssessment>>>;
export type GetEnrollmentAssessmentQueryError = ErrorType<ErrorResponse>;

export function useGetEnrollmentAssessment<
  TData = Awaited<ReturnType<typeof getEnrollmentAssessment>>,
  TError = ErrorType<ErrorResponse>,
>(
  npi: string,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getEnrollmentAssessment>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getEnrollmentAssessment>>,
          TError,
          Awaited<ReturnType<typeof getEnrollmentAssessment>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetEnrollmentAssessment<
  TData = Awaited<ReturnType<typeof getEnrollmentAssessment>>,
  TError = ErrorType<ErrorResponse>,
>(
  npi: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getEnrollmentAssessment>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getEnrollmentAssessment>>,
          TError,
          Awaited<ReturnType<typeof getEnrollmentAssessment>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetEnrollmentAssessment<
  TData = Awaited<ReturnType<typeof getEnrollmentAssessment>>,
  TError = ErrorType<ErrorResponse>,
>(
  npi: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getEnrollmentAssessment>>, TError, TData>>;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Read assessment materials, answers and feedback
 */

export function useGetEnrollmentAssessment<
  TData = Awaited<ReturnType<typeof getEnrollmentAssessment>>,
  TError = ErrorType<ErrorResponse>,
>(
  npi: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getEnrollmentAssessment>>, TError, TData>>;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetEnrollmentAssessmentQueryOptions(npi, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

export const getStartEnrollmentAssessmentUrl = (npi: string) => {
  return `/assessments/${npi}`;
};

/**
 * Prescriber-only: the signed-in account must belong to the prescriber selected by NPI. Staff, AES and other prescribers cannot use this operation. Requires all three preparation sections confirmed and current reference review. Resumes an unfinished attempt; otherwise begins the eligible attempt. After three failures, renewed review and the next attempt are saved together. Starting consumes no attempt. Returns 409 ENROLLMENT_CHANGED, ENROLLMENT_NOT_READY, ENROLLMENT_REQUEST_CONFLICT for reused keys with changed input, or ENROLLMENT_FINALIZED. Contract placeholder: valid requests return 501 NOT_IMPLEMENTED after session, active-account, CSRF and input checks. Target-specific permissions are documented here and must be enforced before live behavior is enabled.
 * @summary Begin, resume, retry or restart the assessment
 */
export const startEnrollmentAssessment = async (
  npi: string,
  startEnrollmentAssessmentInput: StartEnrollmentAssessmentInput,
  options?: Parameters<typeof apiFetch>[1],
): Promise<AssessmentView> => {
  const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
  return apiFetch<AssessmentView>(getStartEnrollmentAssessmentUrl(npi), {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(startEnrollmentAssessmentInput),
  });
};

export const getStartEnrollmentAssessmentMutationKey = () => ['startEnrollmentAssessment'] as const;

export const getStartEnrollmentAssessmentMutationOptions = <
  TError = ErrorType<ErrorResponse | EnrollmentConflict>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof startEnrollmentAssessment>>,
    TError,
    StartEnrollmentAssessmentMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof apiFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof startEnrollmentAssessment>>,
  TError,
  StartEnrollmentAssessmentMutationVariables,
  TContext
> => {
  const mutationKey = getStartEnrollmentAssessmentMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof startEnrollmentAssessment>>,
    StartEnrollmentAssessmentMutationVariables
  > = (props) => {
    const { npi, data } = props ?? {};

    return startEnrollmentAssessment(npi, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type StartEnrollmentAssessmentMutationResult = NonNullable<
  Awaited<ReturnType<typeof startEnrollmentAssessment>>
>;
export type StartEnrollmentAssessmentMutationBody = StartEnrollmentAssessmentInput;
export type StartEnrollmentAssessmentMutationError = ErrorType<ErrorResponse | EnrollmentConflict>;
export type StartEnrollmentAssessmentMutationVariables = { npi: string; data: StartEnrollmentAssessmentInput };

/**
 * @summary Begin, resume, retry or restart the assessment
 */
export const useStartEnrollmentAssessment = <
  TError = ErrorType<ErrorResponse | EnrollmentConflict>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof startEnrollmentAssessment>>,
      TError,
      StartEnrollmentAssessmentMutationVariables,
      TContext
    >;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof startEnrollmentAssessment>>,
  TError,
  StartEnrollmentAssessmentMutationVariables,
  TContext
> => {
  return useMutation(getStartEnrollmentAssessmentMutationOptions(options), queryClient);
};

export const getRecordEnrollmentMaterialOpenedUrl = (
  npi: string,
  materialId: 'prescribing_information' | 'prescriber_training' | 'rems_program_overview',
) => {
  return `/assessments/${npi}/material-openings/${materialId}`;
};

/**
 * Prescriber-only: the signed-in account must belong to the prescriber selected by NPI. Staff, AES and other prescribers cannot use this operation. Records the opening for the supplied review token and material. Repeated openings for the same review do not duplicate evidence or consume attempts. An opening records activity, not comprehension. Returns 409 ENROLLMENT_CHANGED for old review context, or ENROLLMENT_FINALIZED. Contract placeholder: valid requests return 501 NOT_IMPLEMENTED after session, active-account, CSRF and input checks. Target-specific permissions are documented here and must be enforced before live behavior is enabled.
 * @summary Record a reference document opening
 */
export const recordEnrollmentMaterialOpened = async (
  npi: string,
  materialId: 'prescribing_information' | 'prescriber_training' | 'rems_program_overview',
  recordEnrollmentMaterialOpenedInput: RecordEnrollmentMaterialOpenedInput,
  options?: Parameters<typeof apiFetch>[1],
): Promise<AssessmentView> => {
  const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
  return apiFetch<AssessmentView>(getRecordEnrollmentMaterialOpenedUrl(npi, materialId), {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(recordEnrollmentMaterialOpenedInput),
  });
};

export const getRecordEnrollmentMaterialOpenedMutationKey = () => ['recordEnrollmentMaterialOpened'] as const;

export const getRecordEnrollmentMaterialOpenedMutationOptions = <
  TError = ErrorType<ErrorResponse | EnrollmentConflict>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof recordEnrollmentMaterialOpened>>,
    TError,
    RecordEnrollmentMaterialOpenedMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof apiFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof recordEnrollmentMaterialOpened>>,
  TError,
  RecordEnrollmentMaterialOpenedMutationVariables,
  TContext
> => {
  const mutationKey = getRecordEnrollmentMaterialOpenedMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof recordEnrollmentMaterialOpened>>,
    RecordEnrollmentMaterialOpenedMutationVariables
  > = (props) => {
    const { npi, materialId, data } = props ?? {};

    return recordEnrollmentMaterialOpened(npi, materialId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type RecordEnrollmentMaterialOpenedMutationResult = NonNullable<
  Awaited<ReturnType<typeof recordEnrollmentMaterialOpened>>
>;
export type RecordEnrollmentMaterialOpenedMutationBody = RecordEnrollmentMaterialOpenedInput;
export type RecordEnrollmentMaterialOpenedMutationError = ErrorType<ErrorResponse | EnrollmentConflict>;
export type RecordEnrollmentMaterialOpenedMutationVariables = {
  npi: string;
  materialId: 'prescribing_information' | 'prescriber_training' | 'rems_program_overview';
  data: RecordEnrollmentMaterialOpenedInput;
};

/**
 * @summary Record a reference document opening
 */
export const useRecordEnrollmentMaterialOpened = <
  TError = ErrorType<ErrorResponse | EnrollmentConflict>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof recordEnrollmentMaterialOpened>>,
      TError,
      RecordEnrollmentMaterialOpenedMutationVariables,
      TContext
    >;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof recordEnrollmentMaterialOpened>>,
  TError,
  RecordEnrollmentMaterialOpenedMutationVariables,
  TContext
> => {
  return useMutation(getRecordEnrollmentMaterialOpenedMutationOptions(options), queryClient);
};

export const getSaveEnrollmentAssessmentDraftUrl = (npi: string) => {
  return `/assessments/${npi}/draft`;
};

/**
 * Prescriber-only: the signed-in account must belong to the prescriber selected by NPI. Staff, AES and other prescribers cannot use this operation. Replaces 0–9 unique draft answers for the supplied session, without scoring or consuming an attempt. True/false answers are strings. Returns 409 ENROLLMENT_CHANGED for old or mismatched context, ENROLLMENT_NOT_READY for an inactive attempt, or ENROLLMENT_FINALIZED. Contract placeholder: valid requests return 501 NOT_IMPLEMENTED after session, active-account, CSRF and input checks. Target-specific permissions are documented here and must be enforced before live behavior is enabled.
 * @summary Save unfinished assessment answers
 */
export const saveEnrollmentAssessmentDraft = async (
  npi: string,
  saveEnrollmentAssessmentDraftInput: SaveEnrollmentAssessmentDraftInput,
  options?: Parameters<typeof apiFetch>[1],
): Promise<AssessmentView> => {
  const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
  return apiFetch<AssessmentView>(getSaveEnrollmentAssessmentDraftUrl(npi), {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(saveEnrollmentAssessmentDraftInput),
  });
};

export const getSaveEnrollmentAssessmentDraftMutationKey = () => ['saveEnrollmentAssessmentDraft'] as const;

export const getSaveEnrollmentAssessmentDraftMutationOptions = <
  TError = ErrorType<ErrorResponse | EnrollmentConflict>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof saveEnrollmentAssessmentDraft>>,
    TError,
    SaveEnrollmentAssessmentDraftMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof apiFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof saveEnrollmentAssessmentDraft>>,
  TError,
  SaveEnrollmentAssessmentDraftMutationVariables,
  TContext
> => {
  const mutationKey = getSaveEnrollmentAssessmentDraftMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof saveEnrollmentAssessmentDraft>>,
    SaveEnrollmentAssessmentDraftMutationVariables
  > = (props) => {
    const { npi, data } = props ?? {};

    return saveEnrollmentAssessmentDraft(npi, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type SaveEnrollmentAssessmentDraftMutationResult = NonNullable<
  Awaited<ReturnType<typeof saveEnrollmentAssessmentDraft>>
>;
export type SaveEnrollmentAssessmentDraftMutationBody = SaveEnrollmentAssessmentDraftInput;
export type SaveEnrollmentAssessmentDraftMutationError = ErrorType<ErrorResponse | EnrollmentConflict>;
export type SaveEnrollmentAssessmentDraftMutationVariables = { npi: string; data: SaveEnrollmentAssessmentDraftInput };

/**
 * @summary Save unfinished assessment answers
 */
export const useSaveEnrollmentAssessmentDraft = <
  TError = ErrorType<ErrorResponse | EnrollmentConflict>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof saveEnrollmentAssessmentDraft>>,
      TError,
      SaveEnrollmentAssessmentDraftMutationVariables,
      TContext
    >;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof saveEnrollmentAssessmentDraft>>,
  TError,
  SaveEnrollmentAssessmentDraftMutationVariables,
  TContext
> => {
  return useMutation(getSaveEnrollmentAssessmentDraftMutationOptions(options), queryClient);
};

export const getSubmitEnrollmentAssessmentUrl = (npi: string) => {
  return `/assessments/${npi}/submit`;
};

/**
 * Prescriber-only: the signed-in account must belong to the prescriber selected by NPI. Staff, AES and other prescribers cannot use this operation. Requires questions 1–9 exactly once. The server scores against the assigned question set; 9/9 passes. A valid accepted submission consumes one of three attempts and queues its unsigned data and PDF for Anovo, passed or failed. Invalid submissions consume none; identical authorized request retries return the saved result. Three failures require fresh document review. Returns 409 ENROLLMENT_CHANGED, ENROLLMENT_NOT_READY, ENROLLMENT_REQUEST_CONFLICT, or ENROLLMENT_FINALIZED. Contract placeholder: valid requests return 501 NOT_IMPLEMENTED after session, active-account, CSRF and input checks. Target-specific permissions are documented here and must be enforced before live behavior is enabled.
 * @summary Submit and score one complete assessment attempt
 */
export const submitEnrollmentAssessment = async (
  npi: string,
  submitEnrollmentAssessmentInput: SubmitEnrollmentAssessmentInput,
  options?: Parameters<typeof apiFetch>[1],
): Promise<AssessmentView> => {
  const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
  return apiFetch<AssessmentView>(getSubmitEnrollmentAssessmentUrl(npi), {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(submitEnrollmentAssessmentInput),
  });
};

export const getSubmitEnrollmentAssessmentMutationKey = () => ['submitEnrollmentAssessment'] as const;

export const getSubmitEnrollmentAssessmentMutationOptions = <
  TError = ErrorType<ErrorResponse | EnrollmentConflict>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof submitEnrollmentAssessment>>,
    TError,
    SubmitEnrollmentAssessmentMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof apiFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof submitEnrollmentAssessment>>,
  TError,
  SubmitEnrollmentAssessmentMutationVariables,
  TContext
> => {
  const mutationKey = getSubmitEnrollmentAssessmentMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof submitEnrollmentAssessment>>,
    SubmitEnrollmentAssessmentMutationVariables
  > = (props) => {
    const { npi, data } = props ?? {};

    return submitEnrollmentAssessment(npi, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type SubmitEnrollmentAssessmentMutationResult = NonNullable<
  Awaited<ReturnType<typeof submitEnrollmentAssessment>>
>;
export type SubmitEnrollmentAssessmentMutationBody = SubmitEnrollmentAssessmentInput;
export type SubmitEnrollmentAssessmentMutationError = ErrorType<ErrorResponse | EnrollmentConflict>;
export type SubmitEnrollmentAssessmentMutationVariables = { npi: string; data: SubmitEnrollmentAssessmentInput };

/**
 * @summary Submit and score one complete assessment attempt
 */
export const useSubmitEnrollmentAssessment = <
  TError = ErrorType<ErrorResponse | EnrollmentConflict>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof submitEnrollmentAssessment>>,
      TError,
      SubmitEnrollmentAssessmentMutationVariables,
      TContext
    >;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof submitEnrollmentAssessment>>,
  TError,
  SubmitEnrollmentAssessmentMutationVariables,
  TContext
> => {
  return useMutation(getSubmitEnrollmentAssessmentMutationOptions(options), queryClient);
};

export const getGetEnrollmentFormUrl = (npi: string) => {
  return `/enrollments/${npi}/form`;
};

/**
 * The prescriber or office staff with current permission to prepare this prescriber's enrollment may use this operation; AES users may not. Prepares the document from confirmed details and a passed assessment. Returns a form token, expiring PDF URL, agreement and terms before terms acceptance. This is not signature evidence. Only the designated prescriber receives their saved checkbox choice. Returns 409 ENROLLMENT_NOT_READY or ENROLLMENT_FORM_CHANGED. Contract placeholder: valid requests return 501 NOT_IMPLEMENTED after session, active-account, CSRF and input checks. Target-specific permissions are documented here and must be enforced before live behavior is enabled.
 * @summary Preview the exact enrollment PDF and agreement
 */
export const getEnrollmentForm = async (
  npi: string,
  options?: Parameters<typeof apiFetch>[1],
): Promise<EnrollmentForm> => {
  return apiFetch<EnrollmentForm>(getGetEnrollmentFormUrl(npi), {
    ...options,
    method: 'GET',
  });
};

export const getGetEnrollmentFormQueryKey = (npi: string) => {
  return [`/enrollments/${npi}/form`] as const;
};

export const getGetEnrollmentFormQueryOptions = <
  TData = Awaited<ReturnType<typeof getEnrollmentForm>>,
  TError = ErrorType<ErrorResponse | EnrollmentConflict>,
>(
  npi: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getEnrollmentForm>>, TError, TData>>;
    request?: SecondParameter<typeof apiFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetEnrollmentFormQueryKey(npi);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getEnrollmentForm>>> = ({ signal }) =>
    getEnrollmentForm(npi, { signal, ...requestOptions });

  return { queryKey, queryFn, enabled: npi !== null && npi !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getEnrollmentForm>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetEnrollmentFormQueryResult = NonNullable<Awaited<ReturnType<typeof getEnrollmentForm>>>;
export type GetEnrollmentFormQueryError = ErrorType<ErrorResponse | EnrollmentConflict>;

export function useGetEnrollmentForm<
  TData = Awaited<ReturnType<typeof getEnrollmentForm>>,
  TError = ErrorType<ErrorResponse | EnrollmentConflict>,
>(
  npi: string,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getEnrollmentForm>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getEnrollmentForm>>,
          TError,
          Awaited<ReturnType<typeof getEnrollmentForm>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetEnrollmentForm<
  TData = Awaited<ReturnType<typeof getEnrollmentForm>>,
  TError = ErrorType<ErrorResponse | EnrollmentConflict>,
>(
  npi: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getEnrollmentForm>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getEnrollmentForm>>,
          TError,
          Awaited<ReturnType<typeof getEnrollmentForm>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetEnrollmentForm<
  TData = Awaited<ReturnType<typeof getEnrollmentForm>>,
  TError = ErrorType<ErrorResponse | EnrollmentConflict>,
>(
  npi: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getEnrollmentForm>>, TError, TData>>;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Preview the exact enrollment PDF and agreement
 */

export function useGetEnrollmentForm<
  TData = Awaited<ReturnType<typeof getEnrollmentForm>>,
  TError = ErrorType<ErrorResponse | EnrollmentConflict>,
>(
  npi: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getEnrollmentForm>>, TError, TData>>;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetEnrollmentFormQueryOptions(npi, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

export const getSaveEnrollmentSignatureDraftUrl = (npi: string) => {
  return `/enrollments/${npi}/signature`;
};

/**
 * Prescriber-only: the signed-in account must belong to the prescriber selected by NPI. Staff, AES and other prescribers cannot use this operation. Saves accepted or unaccepted terms for the current form. This neither starts a provider session nor proves the form is signed. Returns 409 ENROLLMENT_CHANGED, ENROLLMENT_FORM_CHANGED, ENROLLMENT_NOT_READY, or ENROLLMENT_FINALIZED. Contract placeholder: valid requests return 501 NOT_IMPLEMENTED after session, active-account, CSRF and input checks. Target-specific permissions are documented here and must be enforced before live behavior is enabled.
 * @summary Save the prescriber's terms checkbox
 */
export const saveEnrollmentSignatureDraft = async (
  npi: string,
  saveEnrollmentSignatureDraftInput: SaveEnrollmentSignatureDraftInput,
  options?: Parameters<typeof apiFetch>[1],
): Promise<EnrollmentView> => {
  const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
  return apiFetch<EnrollmentView>(getSaveEnrollmentSignatureDraftUrl(npi), {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(saveEnrollmentSignatureDraftInput),
  });
};

export const getSaveEnrollmentSignatureDraftMutationKey = () => ['saveEnrollmentSignatureDraft'] as const;

export const getSaveEnrollmentSignatureDraftMutationOptions = <
  TError = ErrorType<ErrorResponse | EnrollmentConflict>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof saveEnrollmentSignatureDraft>>,
    TError,
    SaveEnrollmentSignatureDraftMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof apiFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof saveEnrollmentSignatureDraft>>,
  TError,
  SaveEnrollmentSignatureDraftMutationVariables,
  TContext
> => {
  const mutationKey = getSaveEnrollmentSignatureDraftMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof saveEnrollmentSignatureDraft>>,
    SaveEnrollmentSignatureDraftMutationVariables
  > = (props) => {
    const { npi, data } = props ?? {};

    return saveEnrollmentSignatureDraft(npi, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type SaveEnrollmentSignatureDraftMutationResult = NonNullable<
  Awaited<ReturnType<typeof saveEnrollmentSignatureDraft>>
>;
export type SaveEnrollmentSignatureDraftMutationBody = SaveEnrollmentSignatureDraftInput;
export type SaveEnrollmentSignatureDraftMutationError = ErrorType<ErrorResponse | EnrollmentConflict>;
export type SaveEnrollmentSignatureDraftMutationVariables = { npi: string; data: SaveEnrollmentSignatureDraftInput };

/**
 * @summary Save the prescriber's terms checkbox
 */
export const useSaveEnrollmentSignatureDraft = <
  TError = ErrorType<ErrorResponse | EnrollmentConflict>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof saveEnrollmentSignatureDraft>>,
      TError,
      SaveEnrollmentSignatureDraftMutationVariables,
      TContext
    >;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof saveEnrollmentSignatureDraft>>,
  TError,
  SaveEnrollmentSignatureDraftMutationVariables,
  TContext
> => {
  return useMutation(getSaveEnrollmentSignatureDraftMutationOptions(options), queryClient);
};

export const getStartEnrollmentSigningUrl = (npi: string) => {
  return `/enrollments/${npi}/signing-sessions`;
};

/**
 * Prescriber-only: the signed-in account must belong to the prescriber selected by NPI. Staff, AES and other prescribers cannot use this operation. Requires termsAccepted=true and the current form token. Returns a provider session URL for this prescriber and document. Only verified provider evidence can establish signed state; browser signer IDs, dates or signed flags are rejected. Returns 409 ENROLLMENT_CHANGED, ENROLLMENT_FORM_CHANGED, ENROLLMENT_NOT_READY, ENROLLMENT_REQUEST_CONFLICT, or ENROLLMENT_FINALIZED; 502 indicates a signing provider failure. Contract placeholder: valid requests return 501 NOT_IMPLEMENTED after session, active-account, CSRF and input checks. Target-specific permissions are documented here and must be enforced before live behavior is enabled.
 * @summary Start or resume signing the previewed enrollment
 */
export const startEnrollmentSigning = async (
  npi: string,
  startEnrollmentSigningInput: StartEnrollmentSigningInput,
  options?: Parameters<typeof apiFetch>[1],
): Promise<EnrollmentSigningSession> => {
  const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
  return apiFetch<EnrollmentSigningSession>(getStartEnrollmentSigningUrl(npi), {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(startEnrollmentSigningInput),
  });
};

export const getStartEnrollmentSigningMutationKey = () => ['startEnrollmentSigning'] as const;

export const getStartEnrollmentSigningMutationOptions = <
  TError = ErrorType<ErrorResponse | EnrollmentConflict>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof startEnrollmentSigning>>,
    TError,
    StartEnrollmentSigningMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof apiFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof startEnrollmentSigning>>,
  TError,
  StartEnrollmentSigningMutationVariables,
  TContext
> => {
  const mutationKey = getStartEnrollmentSigningMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof startEnrollmentSigning>>,
    StartEnrollmentSigningMutationVariables
  > = (props) => {
    const { npi, data } = props ?? {};

    return startEnrollmentSigning(npi, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type StartEnrollmentSigningMutationResult = NonNullable<Awaited<ReturnType<typeof startEnrollmentSigning>>>;
export type StartEnrollmentSigningMutationBody = StartEnrollmentSigningInput;
export type StartEnrollmentSigningMutationError = ErrorType<ErrorResponse | EnrollmentConflict>;
export type StartEnrollmentSigningMutationVariables = { npi: string; data: StartEnrollmentSigningInput };

/**
 * @summary Start or resume signing the previewed enrollment
 */
export const useStartEnrollmentSigning = <TError = ErrorType<ErrorResponse | EnrollmentConflict>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof startEnrollmentSigning>>,
      TError,
      StartEnrollmentSigningMutationVariables,
      TContext
    >;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof startEnrollmentSigning>>,
  TError,
  StartEnrollmentSigningMutationVariables,
  TContext
> => {
  return useMutation(getStartEnrollmentSigningMutationOptions(options), queryClient);
};

export const getSubmitPrescriberEnrollmentUrl = (npi: string) => {
  return `/enrollments/${npi}/submit`;
};

/**
 * Prescriber-only: the signed-in account must belong to the prescriber selected by NPI. Staff, AES and other prescribers cannot use this operation. Checks the current completed form and its matching verified signature, then freezes the package. 202 means work recorded locally, not partner receipt or certification approval. Unverified NPI ownership returns identity_verification_required and holds the final package. An authorized retry with the same key/input returns its original result even after later rejection. Read enrollment submission progress for asynchronous rejections. A new request against a known rejection may return 422: APIE024 (NPI not found), APIE076 (NPI not valid for certification), APIE021 (NPI already registered), or APIE075 (certification failed), each with the exact Anovo message. Timeouts are not rejections. Returns 409 ENROLLMENT_CHANGED, ENROLLMENT_FORM_CHANGED, ENROLLMENT_INCOMPLETE with missingFields, ENROLLMENT_NOT_READY, ENROLLMENT_REQUEST_CONFLICT, or ENROLLMENT_FINALIZED. Contract placeholder: valid requests return 501 NOT_IMPLEMENTED after session, active-account, CSRF and input checks. Target-specific permissions are documented here and must be enforced before live behavior is enabled.
 * @summary Submit the signed prescriber enrollment
 */
export const submitPrescriberEnrollment = async (
  npi: string,
  submitPrescriberEnrollmentInput: SubmitPrescriberEnrollmentInput,
  options?: Parameters<typeof apiFetch>[1],
): Promise<EnrollmentSubmission> => {
  const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
  return apiFetch<EnrollmentSubmission>(getSubmitPrescriberEnrollmentUrl(npi), {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(submitPrescriberEnrollmentInput),
  });
};

export const getSubmitPrescriberEnrollmentMutationKey = () => ['submitPrescriberEnrollment'] as const;

export const getSubmitPrescriberEnrollmentMutationOptions = <
  TError = ErrorType<ErrorResponse | EnrollmentConflict | EnrollmentRejection>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof submitPrescriberEnrollment>>,
    TError,
    SubmitPrescriberEnrollmentMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof apiFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof submitPrescriberEnrollment>>,
  TError,
  SubmitPrescriberEnrollmentMutationVariables,
  TContext
> => {
  const mutationKey = getSubmitPrescriberEnrollmentMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof submitPrescriberEnrollment>>,
    SubmitPrescriberEnrollmentMutationVariables
  > = (props) => {
    const { npi, data } = props ?? {};

    return submitPrescriberEnrollment(npi, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type SubmitPrescriberEnrollmentMutationResult = NonNullable<
  Awaited<ReturnType<typeof submitPrescriberEnrollment>>
>;
export type SubmitPrescriberEnrollmentMutationBody = SubmitPrescriberEnrollmentInput;
export type SubmitPrescriberEnrollmentMutationError = ErrorType<
  ErrorResponse | EnrollmentConflict | EnrollmentRejection
>;
export type SubmitPrescriberEnrollmentMutationVariables = { npi: string; data: SubmitPrescriberEnrollmentInput };

/**
 * @summary Submit the signed prescriber enrollment
 */
export const useSubmitPrescriberEnrollment = <
  TError = ErrorType<ErrorResponse | EnrollmentConflict | EnrollmentRejection>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof submitPrescriberEnrollment>>,
      TError,
      SubmitPrescriberEnrollmentMutationVariables,
      TContext
    >;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof submitPrescriberEnrollment>>,
  TError,
  SubmitPrescriberEnrollmentMutationVariables,
  TContext
> => {
  return useMutation(getSubmitPrescriberEnrollmentMutationOptions(options), queryClient);
};

export const getGetMyCertificationStatusUrl = () => {
  return `/me/certification`;
};

/**
 * Prescriber-only: the signed-in account must belong to the prescriber selected by NPI. Staff, AES and other prescribers cannot use this operation. Resolves the NPI from the caller's current account link and reads the latest locally stored status, separate substatus and freshness. A missing prescriber link returns 404. Known prescribers without results return unknown with null partner facts. This does not trigger a live Anovo call. Contract placeholder: valid requests return 501 NOT_IMPLEMENTED after session, active-account, CSRF and input checks. Target-specific permissions are documented here and must be enforced before live behavior is enabled.
 * @summary Read the signed-in prescriber's certification badge
 */
export const getMyCertificationStatus = async (
  options?: Parameters<typeof apiFetch>[1],
): Promise<CertificationStatus> => {
  return apiFetch<CertificationStatus>(getGetMyCertificationStatusUrl(), {
    ...options,
    method: 'GET',
  });
};

export const getGetMyCertificationStatusQueryKey = () => {
  return [`/me/certification`] as const;
};

export const getGetMyCertificationStatusQueryOptions = <
  TData = Awaited<ReturnType<typeof getMyCertificationStatus>>,
  TError = ErrorType<ErrorResponse>,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyCertificationStatus>>, TError, TData>>;
  request?: SecondParameter<typeof apiFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetMyCertificationStatusQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getMyCertificationStatus>>> = ({ signal }) =>
    getMyCertificationStatus({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getMyCertificationStatus>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetMyCertificationStatusQueryResult = NonNullable<Awaited<ReturnType<typeof getMyCertificationStatus>>>;
export type GetMyCertificationStatusQueryError = ErrorType<ErrorResponse>;

export function useGetMyCertificationStatus<
  TData = Awaited<ReturnType<typeof getMyCertificationStatus>>,
  TError = ErrorType<ErrorResponse>,
>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyCertificationStatus>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMyCertificationStatus>>,
          TError,
          Awaited<ReturnType<typeof getMyCertificationStatus>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetMyCertificationStatus<
  TData = Awaited<ReturnType<typeof getMyCertificationStatus>>,
  TError = ErrorType<ErrorResponse>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyCertificationStatus>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMyCertificationStatus>>,
          TError,
          Awaited<ReturnType<typeof getMyCertificationStatus>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetMyCertificationStatus<
  TData = Awaited<ReturnType<typeof getMyCertificationStatus>>,
  TError = ErrorType<ErrorResponse>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyCertificationStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Read the signed-in prescriber's certification badge
 */

export function useGetMyCertificationStatus<
  TData = Awaited<ReturnType<typeof getMyCertificationStatus>>,
  TError = ErrorType<ErrorResponse>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyCertificationStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetMyCertificationStatusQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

export const getGetPrescriberCertificationStatusUrl = (npi: string) => {
  return `/prescribers/${npi}/certification`;
};

/**
 * Available to the prescriber, office staff whose current permissions allow viewing this prescriber, or an active AES account. Active AES users may read every prescriber's certification status; territory only sets the default list view and filter, and does not restrict access. Returns the same locally stored badge facts as the self read, even when the target has not recently logged in. Unknown prescribers return 404. Unknown partner status strings remain readable; Pending means action needed, not routine processing. Contract placeholder: valid requests return 501 NOT_IMPLEMENTED after session, active-account, CSRF and input checks. Target-specific permissions are documented here and must be enforced before live behavior is enabled.
 * @summary Read a permitted prescriber's certification badge
 */
export const getPrescriberCertificationStatus = async (
  npi: string,
  options?: Parameters<typeof apiFetch>[1],
): Promise<CertificationStatus> => {
  return apiFetch<CertificationStatus>(getGetPrescriberCertificationStatusUrl(npi), {
    ...options,
    method: 'GET',
  });
};

export const getGetPrescriberCertificationStatusQueryKey = (npi: string) => {
  return [`/prescribers/${npi}/certification`] as const;
};

export const getGetPrescriberCertificationStatusQueryOptions = <
  TData = Awaited<ReturnType<typeof getPrescriberCertificationStatus>>,
  TError = ErrorType<ErrorResponse>,
>(
  npi: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPrescriberCertificationStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetPrescriberCertificationStatusQueryKey(npi);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getPrescriberCertificationStatus>>> = ({ signal }) =>
    getPrescriberCertificationStatus(npi, { signal, ...requestOptions });

  return { queryKey, queryFn, enabled: npi !== null && npi !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getPrescriberCertificationStatus>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetPrescriberCertificationStatusQueryResult = NonNullable<
  Awaited<ReturnType<typeof getPrescriberCertificationStatus>>
>;
export type GetPrescriberCertificationStatusQueryError = ErrorType<ErrorResponse>;

export function useGetPrescriberCertificationStatus<
  TData = Awaited<ReturnType<typeof getPrescriberCertificationStatus>>,
  TError = ErrorType<ErrorResponse>,
>(
  npi: string,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPrescriberCertificationStatus>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getPrescriberCertificationStatus>>,
          TError,
          Awaited<ReturnType<typeof getPrescriberCertificationStatus>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetPrescriberCertificationStatus<
  TData = Awaited<ReturnType<typeof getPrescriberCertificationStatus>>,
  TError = ErrorType<ErrorResponse>,
>(
  npi: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPrescriberCertificationStatus>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getPrescriberCertificationStatus>>,
          TError,
          Awaited<ReturnType<typeof getPrescriberCertificationStatus>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetPrescriberCertificationStatus<
  TData = Awaited<ReturnType<typeof getPrescriberCertificationStatus>>,
  TError = ErrorType<ErrorResponse>,
>(
  npi: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPrescriberCertificationStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Read a permitted prescriber's certification badge
 */

export function useGetPrescriberCertificationStatus<
  TData = Awaited<ReturnType<typeof getPrescriberCertificationStatus>>,
  TError = ErrorType<ErrorResponse>,
>(
  npi: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPrescriberCertificationStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetPrescriberCertificationStatusQueryOptions(npi, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

export const getListExamplesUrl = () => {
  return `/examples`;
};

/**
 * Returns every example.
 * @summary List examples
 */
export const listExamples = async (options?: Parameters<typeof apiFetch>[1]): Promise<Example[]> => {
  return apiFetch<Example[]>(getListExamplesUrl(), {
    ...options,
    method: 'GET',
  });
};

export const getListExamplesQueryKey = () => {
  return [`/examples`] as const;
};

export const getListExamplesQueryOptions = <
  TData = Awaited<ReturnType<typeof listExamples>>,
  TError = ErrorType<ErrorResponse>,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listExamples>>, TError, TData>>;
  request?: SecondParameter<typeof apiFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListExamplesQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listExamples>>> = ({ signal }) =>
    listExamples({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listExamples>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListExamplesQueryResult = NonNullable<Awaited<ReturnType<typeof listExamples>>>;
export type ListExamplesQueryError = ErrorType<ErrorResponse>;

export function useListExamples<TData = Awaited<ReturnType<typeof listExamples>>, TError = ErrorType<ErrorResponse>>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listExamples>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listExamples>>,
          TError,
          Awaited<ReturnType<typeof listExamples>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListExamples<TData = Awaited<ReturnType<typeof listExamples>>, TError = ErrorType<ErrorResponse>>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listExamples>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listExamples>>,
          TError,
          Awaited<ReturnType<typeof listExamples>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListExamples<TData = Awaited<ReturnType<typeof listExamples>>, TError = ErrorType<ErrorResponse>>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listExamples>>, TError, TData>>;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List examples
 */

export function useListExamples<TData = Awaited<ReturnType<typeof listExamples>>, TError = ErrorType<ErrorResponse>>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listExamples>>, TError, TData>>;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListExamplesQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

export const getCreateExampleUrl = () => {
  return `/examples`;
};

/**
 * Creates an example and returns it.
 * @summary Create an example
 */
export const createExample = async (
  createExampleInput: CreateExampleInput,
  options?: Parameters<typeof apiFetch>[1],
): Promise<Example> => {
  const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
  return apiFetch<Example>(getCreateExampleUrl(), {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(createExampleInput),
  });
};

export const getCreateExampleMutationKey = () => ['createExample'] as const;

export const getCreateExampleMutationOptions = <TError = ErrorType<ErrorResponse>, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createExample>>,
    TError,
    CreateExampleMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof apiFetch>;
}): UseMutationOptions<Awaited<ReturnType<typeof createExample>>, TError, CreateExampleMutationVariables, TContext> => {
  const mutationKey = getCreateExampleMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<Awaited<ReturnType<typeof createExample>>, CreateExampleMutationVariables> = (
    props,
  ) => {
    const { data } = props ?? {};

    return createExample(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateExampleMutationResult = NonNullable<Awaited<ReturnType<typeof createExample>>>;
export type CreateExampleMutationBody = CreateExampleInput;
export type CreateExampleMutationError = ErrorType<ErrorResponse>;
export type CreateExampleMutationVariables = { data: CreateExampleInput };

/**
 * @summary Create an example
 */
export const useCreateExample = <TError = ErrorType<ErrorResponse>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createExample>>,
      TError,
      CreateExampleMutationVariables,
      TContext
    >;
    request?: SecondParameter<typeof apiFetch>;
  },
  queryClient?: QueryClient,
): UseMutationResult<Awaited<ReturnType<typeof createExample>>, TError, CreateExampleMutationVariables, TContext> => {
  return useMutation(getCreateExampleMutationOptions(options), queryClient);
};
